import { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const DEFAULT_PARTNERS = [
  { name: 'LEGO',          img: 'https://storage.googleapis.com/uxpilot-auth.appspot.com/73bf727319-88749e973d3531863856.png',  accent: 'hover:border-primary/30'  },
  { name: 'Fisher-Price',  img: 'https://storage.googleapis.com/uxpilot-auth.appspot.com/beb9b98a8d-f229b25b7ccb6a3b29d7.png',  accent: 'hover:border-accent2/30'  },
  { name: 'Hasbro',        img: 'https://storage.googleapis.com/uxpilot-auth.appspot.com/d2d39ddec4-ef010666a0328ed5badf.png',  accent: 'hover:border-accent1/30'  },
  { name: 'Mattel',        img: 'https://storage.googleapis.com/uxpilot-auth.appspot.com/341010fbd0-5e3764992e0105f791f3.png',  accent: 'hover:border-accent3/30'  },
  { name: 'Hot Wheels',    img: 'https://storage.googleapis.com/uxpilot-auth.appspot.com/3282cb60f0-fff80fff0cd518ae48b4.png',  accent: 'hover:border-yellow-400/30' },
  { name: 'Scholastic',    img: 'https://storage.googleapis.com/uxpilot-auth.appspot.com/ebd5b6e427-887889061e25c885e179.png',  accent: 'hover:border-primary/30'  },
  { name: 'VTech',         img: 'https://storage.googleapis.com/uxpilot-auth.appspot.com/e79c7fc573-f16278a4e8b7e5b5ae2d.png',  accent: 'hover:border-accent3/30'  },
  { name: 'Playmobil',     img: 'https://storage.googleapis.com/uxpilot-auth.appspot.com/5fc13b1524-1e2743ac5f516ad091ff.png',  accent: 'hover:border-accent1/30'  },
  { name: 'LeapFrog',      img: 'https://storage.googleapis.com/uxpilot-auth.appspot.com/15f686c90b-9d53aae935d844908bf0.png',  accent: 'hover:border-accent2/30'  },
  { name: 'Melissa & Doug',img: 'https://storage.googleapis.com/uxpilot-auth.appspot.com/7703d22d18-37a8d3afa24c8c052acc.png',  accent: 'hover:border-primary/30'  },
];

function getVisibleCount() {
  if (typeof window === 'undefined') return 5;
  if (window.innerWidth < 640)  return 2;
  if (window.innerWidth < 1024) return 3;
  return 5;
}

export default function PartnersSection({
  title    = 'Trusted By Top Brands',
  subtitle = "We partner with the world's most loved kids brands",
  badge    = '🤝 Our Partners',
  partners = DEFAULT_PARTNERS,
}) {
  const trackRef   = useRef(null);
  const timerRef   = useRef(null);
  const [slide,    setSlide]    = useState(0);
  const [visible,  setVisible]  = useState(getVisibleCount);
  const [itemW,    setItemW]    = useState(200); // card width + gap

  const totalSlides = Math.ceil(partners.length / visible);

  // Measure card width after mount and on resize
  const measure = useCallback(() => {
    const v = getVisibleCount();
    setVisible(v);
    const track = trackRef.current;
    if (track) {
      const first = track.querySelector('.logo-item');
      if (first) setItemW(first.offsetWidth + 24); // gap-6 = 24px
    }
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  // Reset slide when visible count changes
  useEffect(() => { setSlide(0); }, [visible]);

  function goTo(idx) {
    setSlide(((idx % totalSlides) + totalSlides) % totalSlides);
  }

  function startAuto() {
    timerRef.current = setInterval(() => setSlide(s => (s + 1) % totalSlides), 3500);
  }
  function resetAuto() {
    clearInterval(timerRef.current);
    startAuto();
  }

  useEffect(() => {
    startAuto();
    return () => clearInterval(timerRef.current);
  }, [totalSlides]);

  const offset = slide * visible * itemW;

  return (
    <section className="py-16 bg-gradient-to-b from-white to-primary/5 border-t border-gray-100 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block bg-primary/10 text-primary font-black text-sm px-5 py-2 rounded-full mb-4">
            {badge}
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-textDark mb-2">{title}</h2>
          <p className="text-textLight text-lg">{subtitle}</p>
        </div>

        {/* Carousel */}
        <div className="relative flex items-center gap-4">

          {/* Prev */}
          <button
            onClick={() => { goTo(slide - 1); resetAuto(); }}
            className="flex-shrink-0 w-11 h-11 bg-white rounded-full shadow-lg border-2 border-primary/20 flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all z-10"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          {/* Track */}
          <div className="flex-1 overflow-hidden">
            <div
              ref={trackRef}
              className="flex items-center gap-6 transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${offset}px)` }}
            >
              {/* Duplicate cards for seamless wrap */}
              {[...partners, ...partners].map((p, i) => (
                <div
                  key={`${p.name}-${i}`}
                  className={`logo-item flex-shrink-0 flex items-center justify-center w-44 h-28 bg-white rounded-2xl shadow-md border-2 border-gray-100 hover:shadow-xl transition-all cursor-pointer p-3 ${p.accent}`}
                >
                  <img
                    src={p.img}
                    alt={p.name}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Next */}
          <button
            onClick={() => { goTo(slide + 1); resetAuto(); }}
            className="flex-shrink-0 w-11 h-11 bg-white rounded-full shadow-lg border-2 border-primary/20 flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all z-10"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-8">
          {Array.from({ length: totalSlides }).map((_, i) => (
            <button
              key={i}
              onClick={() => { goTo(i); resetAuto(); }}
              className={`rounded-full transition-all duration-300 h-2.5 ${i === slide ? 'bg-primary w-6' : 'bg-gray-300 w-2.5 hover:bg-gray-400'}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
